import { useState, useEffect, useCallback, useRef } from "react";
import { useGoogleOAuth } from "@react-oauth/google";

interface UseGoogleIdTokenProps {
	onSuccess: (idToken: string) => void;
	onError?: (error: Error) => void;
}

// Extend the global Window interface to include Google Identity Services
declare global {
	interface Window {
		google?: {
			accounts: {
				oauth2: {
					initTokenClient: (config: {
						client_id: string;
						scope: string;
						callback: (response: {
							credential?: string;
							access_token?: string;
							error?: unknown;
						}) => void;
						error_callback?: (nonOAuthError: {
							type: string;
							message: string;
						}) => void;
					}) => {
						requestAccessToken: () => void;
					};
					initIdTokenClient: (config: {
						client_id: string;
						callback: (response: { credential: string }) => void;
					}) => {
						requestAccessToken: () => void;
					};
				};
			};
		};
	}
}

interface TokenClient {
	requestAccessToken: () => void;
}

export const useGoogleIdToken = ({
	onSuccess,
	onError,
}: UseGoogleIdTokenProps) => {
	const onSuccessRef = useRef(onSuccess);
	const onErrorRef = useRef(onError);

	useEffect(() => {
		onSuccessRef.current = onSuccess;
		onErrorRef.current = onError;
	}, [onSuccess, onError]);

	const { clientId, scriptLoadedSuccessfully } = useGoogleOAuth();
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<Error | null>(null);
	const [tokenClient, setTokenClient] = useState<TokenClient | null>(null);

	useEffect(() => {
		if (!clientId) {
			console.error("Google Client ID is not configured");
			setTokenClient(null);
			return;
		}

		// Wait until GoogleOAuthProvider finishes loading gsi/client.
		// Re-runs when scriptLoadedSuccessfully flips true (fixes the one-shot race).
		if (!scriptLoadedSuccessfully || !window.google?.accounts?.oauth2) {
			setTokenClient(null);
			return;
		}

		try {
			const client = window.google.accounts.oauth2.initTokenClient({
				client_id: clientId,
				scope: "email profile openid",
				callback: (response) => {
					setIsLoading(false);
					if (response.access_token) {
						onSuccessRef.current(response.access_token);
					} else {
						const err = new Error("No access token received from Google");
						setError(err);
						onErrorRef.current?.(err);
					}
				},
				error_callback: (nonOAuthError) => {
					setIsLoading(false);
					// User dismissed the popup — not a real failure; avoid console.error / Grafana noise.
					if (nonOAuthError.type === "popup_closed") {
						console.warn("Google login popup closed");
						return;
					}
					const err = new Error("Google Login Error");
					console.error("Google Login Error Callback", nonOAuthError);
					setError(err);
					onErrorRef.current?.(err);
				},
			});
			setTokenClient(client);
		} catch (err) {
			console.error("Failed to initialize Google Token Client", err);
			setTokenClient(null);
			setError(err as Error);
		}
	}, [clientId, scriptLoadedSuccessfully]);

	const isReady = !!tokenClient;

	const login = useCallback(() => {
		if (!clientId) {
			const err = new Error("Google Client ID is not configured");
			console.error(err.message);
			onErrorRef.current?.(err);
			return;
		}

		if (!tokenClient) {
			const err = new Error("Google Token Client not ready yet");
			console.warn(err.message);
			onErrorRef.current?.(err);
			return;
		}

		setIsLoading(true);
		setError(null);

		// requestAccessToken triggers the popup synchronously
		// This must be called directly from the user click event handler
		tokenClient.requestAccessToken();
	}, [clientId, tokenClient]);

	return { login, isLoading, isReady, error };
};
